import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';

// Granola-only meeting enrichment (MVP migration 123). Present on site='granola' rows, null
// everywhere else. A participant is only ever a real roster member (the compactor never invents
// one); an empty array is valid and expected when no one could be confidently attributed.
export interface MeetingParticipant {
  name: string;
  email: string | null;
  contribution: string | null;
}

export interface SpeakerStats {
  n_speakers: number | null;
  turn_count: number;
  owner_share: number | null; // fraction of turns on the note-owner's mic, 0..1
  start_offset: number | null;
  end_offset: number | null;
  duration_seconds: number | null;
}

export interface ConversationDetailData {
  id: string;
  conversation_title: string | null;
  topic: string;
  summary: string;
  site: string;
  compacted_text: string;
  estimated_tokens: number;
  turn_count: number;
  entity_tags: string[];
  first_captured_at: string;
  last_captured_at: string;
  private: boolean;
  project_name: string | null;
  // Granola-only; nullish elsewhere.
  meeting_participants?: MeetingParticipant[] | null;
  speaker_stats?: SpeakerStats | null;
  // Conversation note redesign (PR1 route additions). note_doc stays `unknown` at the hook layer —
  // deriveNoteDoc (src/lib/note-doc.ts) is the narrow waist that validates/derives before render.
  note_doc?: unknown;
  briefing_category?: string | null;
  entry_kind?: string | null;
  // PR-3 "Open in source" (brief-phase1 spec §2): server-derived source-first deep link. Null for
  // ai-chat/claude-code notes (owner-only platform links), for a teammate's email note (the thread
  // id is per-mailbox), and whenever the viewer-scoped mirror join finds nothing — this note page
  // is itself the designed fallback, so the button simply doesn't render.
  source_url?: string | null;
}

export function useConversationDetail(id: string) {
  const query = useQuery({
    queryKey: ['surfaces', 'conversations', id],
    queryFn: () => apiFetch<ConversationDetailData>(`/api/surfaces/conversations/${id}`),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id,
  });
  return { ...query, data: query.data ?? undefined };
}
